/**
 * [TD-47.6c-D.3] GoldSilverRibbon - Spec §3.5 alert renderer.
 *
 * Mounts into the Canvas v2 `canvas-gold-silver-socket` slot reserved in
 * D.2. Two visual variants:
 *
 *   GOLD   - target is the highest-ranked available player in its tier.
 *            Renders a bright gold-bar with the "Highest ranked …" copy
 *            and (optionally) a paired silver line for next-in-tier or
 *            cross-tier drop context.
 *
 *   SILVER - at least one higher-ranked player at the same tier is
 *            still available. Renders a silver bar with the "X is still
 *            available …" copy. No gold bar.
 *
 * No alert (alert === null) → renders nothing. The Canvas's reserved
 * socket frame stays in place so layout doesn't jitter.
 *
 * Per A.7 Analytics Narrative Discipline Guard: copy comes from the
 * backend derivation; this component does not invent any phrasing.
 */
import { useTheme } from "../../contexts/ThemeContext";

const GOLD_BG = "linear-gradient(135deg, #FFD66B 0%, #F5A623 100%)";
const SILVER_BG = "linear-gradient(135deg, #DBDBDB 0%, #9C9C9C 100%)";

export function GoldSilverRibbon({ alert }) {
  const { isDark } = useTheme();
  if (!alert) {
    return null;
  }

  const subColor = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.62)";

  const gold = alert.gold;
  const silver = alert.silver;

  return (
    <div
      data-testid="gold-silver-ribbon"
      data-alert-kind={alert.kind}
      className="flex flex-col gap-1.5 h-full justify-center"
    >
      {gold && (
        <div
          data-testid="gold-silver-ribbon-gold"
          className="rounded-md px-2 py-1.5 text-center"
          style={{
            background: GOLD_BG,
            color: "#1A1303",
            boxShadow: "0 1px 4px rgba(245, 166, 35, 0.45)",
          }}
        >
          <div className="font-display font-black text-[10px] uppercase tracking-widest">
            Gold
          </div>
          <div
            className="font-display font-bold text-[11px] leading-tight mt-0.5"
            data-testid="gold-silver-ribbon-gold-copy"
          >
            {gold.copy}
          </div>
        </div>
      )}
      {silver && (
        <div
          data-testid="gold-silver-ribbon-silver"
          data-silver-kind={silver.kind}
          className="rounded-md px-2 py-1.5 text-center"
          style={{
            background: SILVER_BG,
            color: "#1A1A1A",
            boxShadow: "0 1px 3px rgba(155, 155, 155, 0.45)",
          }}
        >
          <div className="font-display font-black text-[10px] uppercase tracking-widest">
            Silver
          </div>
          <div
            className="font-display font-bold text-[11px] leading-tight mt-0.5"
            data-testid="gold-silver-ribbon-silver-copy"
          >
            {silver.copy}
          </div>
        </div>
      )}
      {!gold && !silver && (
        <div
          data-testid="gold-silver-ribbon-empty"
          className="font-body text-[11px] italic text-center"
          style={{ color: subColor }}
        >
          No alert.
        </div>
      )}
    </div>
  );
}

export default GoldSilverRibbon;
